import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, Sunset, History, MessageCircle, Send } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

function ShareRow({ rows, direct }) {
  const [day, setDay] = useState(rows[0]?.date || "");
  const [busy, setBusy] = useState(false);
  const row = rows.find(r => r.date === day);
  const openWa = async () => {
    const w = window.open("", "_blank");
    try { const { data } = await api.get(`/office-summary/${day}/share-text`); if (w) w.location.href = data.wa_url; else window.location.href = data.wa_url; }
    catch (e) { w?.close(); toast.error("Could not build message", { description: e.response?.data?.detail || e.message }); }
  };
  const sendTeam = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/office-summary/${day}/share`);
      toast.success(`Sent to ${data.sent.length} team member${data.sent.length === 1 ? "" : "s"}`, { description: data.failed.length ? `Failed: ${data.failed.join(", ")}` : data.sent.join(", ") });
      row.shared_at = data.shared_at; setDay(day);
    } catch (e) { toast.error("WhatsApp send failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-zinc-100 bg-zinc-50" data-testid="office-summary-share">
      <MessageCircle size={14} className="text-emerald-600" />
      <span className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">Share on WhatsApp</span>
      <select data-testid="office-summary-share-day" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 text-xs border border-zinc-300 bg-white px-2">
        {rows.map(r => <option key={r.date} value={r.date}>{r.date}{r.shared_at ? " · shared" : ""}</option>)}
      </select>
      <div className="flex-1" />
      <Button data-testid="office-summary-share-open" size="sm" variant="outline" onClick={openWa} className="rounded-none h-8 text-xs border-emerald-600 text-emerald-700 hover:bg-emerald-50"><MessageCircle size={13} className="mr-1" /> Open WhatsApp</Button>
      {direct ? <Button data-testid="office-summary-share-team" size="sm" onClick={sendTeam} disabled={busy} className="rounded-none h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"><Send size={13} className="mr-1" />{busy ? "Sending…" : "Send to team"}</Button> : null}
      {row?.shared_at ? <span className="w-full text-[11px] text-zinc-500" data-testid="office-summary-shared-at">Last sent {new Date(row.shared_at).toLocaleString("en-IN")}{row.shared_to?.length ? ` → ${row.shared_to.join(", ")}` : ""}</span> : null}
    </div>
  );
}

function TrendChart({ rows }) {
  const data = [...rows].reverse().map(r => ({
    day: r.date.slice(5), done: r.totals.completed_today, overdue: r.totals.overdue_cases,
    money: (r.money.invoices_paid_amount || 0) + (r.money.khata_collected || 0), raised: r.money.invoices_raised_amount || 0,
  }));
  const busiest = data.reduce((a, b) => (b.done > (a?.done ?? -1) ? b : a), null);
  const slowest = data.reduce((a, b) => (b.done < (a?.done ?? Infinity) ? b : a), null);
  return (
    <div className="px-4 py-3 border-t border-zinc-100" data-testid="office-summary-trend">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold flex items-center gap-1"><History size={12} /> 14-day trend · tasks completed &amp; money in</div>
        {data.length > 1 && busiest.done !== slowest.done ? <div className="text-[11px] text-zinc-500">Busiest <b className="text-zinc-900">{busiest.day}</b> ({busiest.done} done) · Slowest <b className="text-zinc-900">{slowest.day}</b> ({slowest.done} done)</div> : null}
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barGap={2}>
            <XAxis dataKey="day" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="l" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <YAxis yAxisId="r" orientation="right" hide />
            <Tooltip cursor={{ fill: "#f4f4f5" }} contentStyle={{ fontSize: 12, borderRadius: 0 }} formatter={(v, n) => n === "Money in" || n === "Raised" ? inr(v) : v} />
            <Bar yAxisId="l" dataKey="done" name="Completed" fill="#18181b" radius={0} />
            <Bar yAxisId="l" dataKey="overdue" name="Overdue" fill="#e11d48" radius={0} />
            <Bar yAxisId="r" dataKey="money" name="Money in" fill="#059669" radius={0} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function OfficeSummaryCard() {
  const [data, setData] = useState(null);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([
    api.get("/office-summary/latest").then(r => setData(r.data)),
    api.get("/office-summary/history?limit=14").then(r => setHist(r.data)),
  ]).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try { await api.post("/office-summary/run"); toast.success("Office summary generated", { description: "A copy was emailed to the admin inbox" }); await load(); }
    catch (e) { toast.error("Could not generate", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const toggleEmail = async () => {
    const email = !data.settings.email;
    await api.put("/office-summary/settings", { email });
    setData({ ...data, settings: { ...data.settings, email } });
  };

  if (!data) return null;
  const s = data.summary;
  const m = s?.money;
  return (
    <section className="border border-zinc-200 bg-white" data-testid="office-summary-card">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-zinc-100">
        <Sunset size={16} className="text-amber-600" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">End-of-day office summary</div>
          <div className="text-xs text-zinc-500">Auto-generated daily at 7:00 PM IST{s ? ` · last: ${s.date} (${s.trigger})${s.emailed ? " · emailed" : ""}` : " · not generated yet"}</div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="office-summary-email-toggle">
          <input type="checkbox" checked={!!data.settings.email} onChange={toggleEmail} className="accent-zinc-900 h-4 w-4" /> <Mail size={13} /> Email to {data.settings.to}
        </label>
        <Button data-testid="office-summary-run" size="sm" onClick={run} disabled={busy} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-8 text-xs"><RefreshCw size={13} className={`mr-1 ${busy ? "animate-spin" : ""}`} />{busy ? "Generating…" : "Generate now"}</Button>
      </div>
      {s ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 divide-x divide-y md:divide-y-0 divide-zinc-100 text-sm tabular" data-testid="office-summary-money">
          <Cell label="Invoices raised" value={`${m.invoices_raised} · ${inr(m.invoices_raised_amount)}`} />
          <Cell label="Payments received" value={`${m.invoices_paid} · ${inr(m.invoices_paid_amount)}`} tone="text-emerald-700" />
          <Cell label="Khata collected" value={inr(m.khata_collected)} tone="text-emerald-700" />
          <Cell label="Khata credit given" value={inr(m.khata_credit_given)} />
          <Cell label="New clients" value={m.new_clients} />
          <Cell label="New leads" value={m.new_leads} />
        </div>
      ) : null}
      {hist.length ? <ShareRow rows={hist} direct={!!data.settings.whatsapp_direct} /> : null}
      {hist.length ? <TrendChart rows={hist} /> : null}
    </section>
  );
}

function Cell({ label, value, tone = "" }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">{label}</div>
      <div className={`font-heading font-bold text-sm md:text-base mt-0.5 truncate ${tone}`}>{value}</div>
    </div>
  );
}
